import { useQuery } from '@tanstack/react-query';
import { ChevronRightIcon, CopyIcon, TrashIcon } from 'lucide-react';
import { type JSX, useState } from 'react';

import { Badge } from '@/components/ui/badge.js';
import { Button } from '@/components/ui/button.js';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible.js';
import {
  activeShares,
  shareUrl,
  sharesQueryOptions,
  useRevokeShare,
  type ShareView,
} from '@/lib/api/shares.js';
import { copyToClipboard } from '@/lib/clipboard.js';
import { safeHttpUrl } from '@/lib/safe-url.js';
import { sharedLinksLabel } from '@/lib/share-reach.js';
import { cn } from '@/lib/utils.js';

import { ConfirmRevokeShareDialog } from './ConfirmRevokeShareDialog.js';

/** Date + time, so links created the same day are distinguishable. */
function formatDateTime(iso: string): string {
  const parsed = new Date(iso);
  if (Number.isNaN(parsed.getTime())) return '';
  return parsed.toLocaleString(undefined, {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/**
 * This conversation's own snapshot links, folded behind one row.
 *
 * Everything else on the tab is a fixed set of controls; the links are the one part that grows for as
 * long as somebody keeps sharing, and left open they pushed the button that creates the next one off the
 * bottom of a small window. So the list is a disclosure, and the closed row carries the count — which is
 * the part people actually read ("is it one or is it nine"), the links themselves being what they open it
 * for.
 *
 * With nothing shared out there is no row at all: the list's own one-line "no active links yet" is the
 * whole answer, and a disclosure that opens onto nothing is a promise of rows that are not coming.
 */
export function ConversationSharesDisclosure({
  conversationId,
  onRevoked,
}: {
  conversationId: string;
  /** Called after a link is really gone, so a caller showing that link can drop it. */
  onRevoked: (shareId: string) => void;
}): JSX.Element {
  const sharesQuery = useQuery(sharesQueryOptions(conversationId));
  const revoke = useRevokeShare();
  const [open, setOpen] = useState(false);
  const [pendingRevokeId, setPendingRevokeId] = useState<string | null>(null);
  const live = activeShares(sharesQuery.data ?? []);

  const list = (
    <ShareList
      shares={live}
      loading={sharesQuery.isLoading}
      loadFailed={sharesQuery.isError}
      onRevoke={setPendingRevokeId}
    />
  );

  const confirm = (
    <ConfirmRevokeShareDialog
      open={pendingRevokeId !== null}
      onOpenChange={(next) => {
        if (!next) setPendingRevokeId(null);
      }}
      pending={revoke.isPending}
      onConfirm={() => {
        const shareId = pendingRevokeId;
        setPendingRevokeId(null);
        if (shareId === null) return;
        revoke.mutate(
          { conversationId, shareId },
          { onSuccess: () => onRevoked(shareId) },
        );
      }}
    />
  );

  if (live.length === 0) {
    return (
      <div className="border-t pt-3">
        {list}
        {confirm}
      </div>
    );
  }

  return (
    <Collapsible
      open={open}
      onOpenChange={setOpen}
      className="border-t pt-3"
      data-testid="conversation-shares-disclosure"
    >
      <CollapsibleTrigger className="flex w-full items-center gap-2 text-left">
        <ChevronRightIcon
          className={cn(
            'size-3.5 shrink-0 text-muted-foreground transition-transform',
            open && 'rotate-90',
          )}
          aria-hidden
        />
        <span className="text-sm">{sharedLinksLabel(live.length)}</span>
      </CollapsibleTrigger>
      <CollapsibleContent className="pt-1">{list}</CollapsibleContent>
      {confirm}
    </Collapsible>
  );
}

/** The links themselves, each with a copy button and a revoke that asks first. */
function ShareList({
  shares,
  loading,
  loadFailed,
  onRevoke,
}: {
  shares: readonly ShareView[];
  /** True during the initial fetch — distinguishes "loading" from "none yet". */
  loading: boolean;
  /** True when the query errored — distinguishes "failed to load" from "none yet". */
  loadFailed: boolean;
  onRevoke: (shareId: string) => void;
}): JSX.Element {
  if (shares.length === 0) {
    let message = 'No active links yet.';
    if (loading) message = 'Loading…';
    else if (loadFailed) message = "Couldn't load links.";
    return <p className="text-xs text-muted-foreground">{message}</p>;
  }

  return (
    <div className="flex flex-col divide-y divide-border/40">
      {shares.map((share) => {
        const url = safeHttpUrl(shareUrl(share.shareId));
        return (
          <div
            key={share.shareId}
            className="flex flex-wrap items-center gap-2 py-2"
          >
            <Badge
              variant={share.accessMode === 'public' ? 'secondary' : 'outline'}
            >
              {share.accessMode === 'public' ? 'Public' : 'Internal'}
            </Badge>
            {share.name ? (
              <span className="min-w-0 truncate text-xs font-medium text-foreground">
                {share.name}
              </span>
            ) : null}
            <span className="text-xs text-muted-foreground">
              {formatDateTime(share.createdAt)}
            </span>
            <div className="ml-auto flex items-center gap-1">
              {url !== '' && (
                <Button
                  variant="ghost"
                  size="icon-sm"
                  aria-label="Copy share link"
                  onClick={() => void copyToClipboard(url, 'Share link copied')}
                >
                  <CopyIcon className="size-4" strokeWidth={1.75} />
                </Button>
              )}
              <Button
                variant="ghost"
                size="icon-sm"
                aria-label="Revoke share link"
                onClick={() => onRevoke(share.shareId)}
              >
                <TrashIcon className="size-4" strokeWidth={1.75} />
              </Button>
            </div>
          </div>
        );
      })}
    </div>
  );
}
